// 26/06 #186: Relatorio de Pausas - lista pausas historicas com filtros e metricas
import React, { useState, useEffect, useContext, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Chip,
  CircularProgress,
  Box,
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  filtersPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  summaryCard: {
    padding: theme.spacing(2),
    textAlign: "center",
    minHeight: 90,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  summaryValue: {
    fontSize: "1.6rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  summaryLabel: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  reasonChip: {
    fontWeight: 600,
    color: "#fff",
  },
}));

const PAGE_SIZE = 50;

const formatDuration = (seconds) => {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
};

const formatDateTime = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const todayIso = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

const daysAgoIso = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

const PauseReport = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [dateFrom, setDateFrom] = useState(daysAgoIso(30));
  const [dateTo, setDateTo] = useState(todayIso());
  const [pauseReasonId, setPauseReasonId] = useState("");
  const [userId, setUserId] = useState("");
  const [queueId, setQueueId] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [topReasons, setTopReasons] = useState([]);
  const [topUsers, setTopUsers] = useState([]);

  // Listas para os filtros
  const [reasons, setReasons] = useState([]);
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);

  // Carregar opcoes de filtro
  useEffect(() => {
    (async () => {
      try {
        const [r, u, q] = await Promise.all([
          api.get("/ticket-pause-reasons?onlyActive=false"),
          api.get("/users/list"),
          api.get("/queue"),
        ]);
        setReasons(r.data || []);
        setUsers(u.data || []);
        setQueues(q.data || []);
      } catch (err) {
        // ignora falha de filtros — usuario sem permissao ainda ve relatorio
      }
    })();
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        dateFrom,
        dateTo,
        page,
        pageSize: PAGE_SIZE,
      };
      if (pauseReasonId) params.pauseReasonId = pauseReasonId;
      if (userId) params.userId = userId;
      if (queueId) params.queueId = queueId;
      if (contactSearch && contactSearch.trim()) params.contactSearch = contactSearch.trim();

      const { data } = await api.get("/reports/pause-logs", { params });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setSummary(data.summary || {});
      setTopReasons(data.topReasons || []);
      setTopUsers(data.topUsers || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, page, pauseReasonId, userId, queueId, contactSearch]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const exportCsv = () => {
    if (!logs.length) return;
    const header = [
      "Ticket",
      "Contato",
      "Fila",
      "Motivo",
      "Observacao",
      "Pausado por",
      "Pausado em",
      "Retorno previsto",
      "Retomado por",
      "Retomado em",
      "Duracao",
      "Tipo de retomada",
    ];
    const rows = logs.map((l) => [
      l.ticketId,
      l.ticket?.contact?.name || "",
      l.ticket?.queue?.name || "",
      l.pauseReasonSnapshotName || l.pauseReason?.name || "",
      (l.pauseNote || "").replace(/\n/g, " "),
      l.pausedByUser?.name || "",
      formatDateTime(l.createdAt),
      formatDateTime(l.pauseUntil),
      l.resumedByUser?.name || "",
      formatDateTime(l.resumedAt),
      l.durationSeconds != null ? formatDuration(l.durationSeconds) : "Em pausa",
      l.resumeType || "",
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `relatorio_pausas_${dateFrom}_a_${dateTo}.csv`;
    a.click();
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <MainContainer>
      <MainHeader>
        <Title>Relatório de Pausas</Title>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {/* Filtros */}
        <Paper className={classes.filtersPaper} variant="outlined">
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="De"
                type="date"
                fullWidth
                value={dateFrom}
                onChange={(e) => { setPage(1); setDateFrom(e.target.value); }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="Até"
                type="date"
                fullWidth
                value={dateTo}
                onChange={(e) => { setPage(1); setDateTo(e.target.value); }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Motivo</InputLabel>
                <Select
                  value={pauseReasonId}
                  onChange={(e) => { setPage(1); setPauseReasonId(e.target.value); }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {reasons.map((r) => (
                    <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Pausado por</InputLabel>
                <Select
                  value={userId}
                  onChange={(e) => { setPage(1); setUserId(e.target.value); }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Fila</InputLabel>
                <Select
                  value={queueId}
                  onChange={(e) => { setPage(1); setQueueId(e.target.value); }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {queues.map((q) => (
                    <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Contato (nome/numero)"
                fullWidth
                value={contactSearch}
                onChange={(e) => { setPage(1); setContactSearch(e.target.value); }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={fetchReport}
                style={{ marginRight: 8 }}
              >
                Filtrar
              </Button>
              <Button variant="outlined" onClick={exportCsv} disabled={!logs.length}>
                Exportar CSV
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Summary cards */}
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={6} sm={3}>
            <Paper className={classes.summaryCard} variant="outlined">
              <div className={classes.summaryValue}>{summary.totalPauses || 0}</div>
              <div className={classes.summaryLabel}>Total de pausas</div>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper className={classes.summaryCard} variant="outlined">
              <div className={classes.summaryValue}>{summary.completedPauses || 0}</div>
              <div className={classes.summaryLabel}>Já retomadas</div>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper className={classes.summaryCard} variant="outlined">
              <div className={classes.summaryValue}>{summary.openPauses || 0}</div>
              <div className={classes.summaryLabel}>Ainda em pausa</div>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper className={classes.summaryCard} variant="outlined">
              <div className={classes.summaryValue}>
                {summary.avgDurationSeconds != null
                  ? formatDuration(summary.avgDurationSeconds)
                  : "—"}
              </div>
              <div className={classes.summaryLabel}>Duração média</div>
            </Paper>
          </Grid>
        </Grid>

        {/* Top motivos + Top usuarios */}
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} md={6}>
            <Paper className={classes.summaryCard} variant="outlined" style={{ textAlign: "left", padding: 16 }}>
              <Typography variant="subtitle2" gutterBottom>Top motivos no período</Typography>
              {topReasons.length === 0 && <Typography variant="body2" color="textSecondary">Sem dados</Typography>}
              {topReasons.map((r) => (
                <Box key={`r-${r.pauseReasonId || r.name}`} mb={0.5}>
                  <Chip
                    size="small"
                    label={`${r.name} — ${r.count}x`}
                    style={{ marginRight: 4 }}
                  />
                  <Typography variant="caption" color="textSecondary">
                    duração total {formatDuration(r.totalDurationSeconds)}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper className={classes.summaryCard} variant="outlined" style={{ textAlign: "left", padding: 16 }}>
              <Typography variant="subtitle2" gutterBottom>Top atendentes que mais pausam</Typography>
              {topUsers.length === 0 && <Typography variant="body2" color="textSecondary">Sem dados</Typography>}
              {topUsers.map((u) => (
                <Box key={`u-${u.userId}`} mb={0.5}>
                  <Chip size="small" label={`${u.name} — ${u.count}x`} />
                </Box>
              ))}
            </Paper>
          </Grid>
        </Grid>

        {/* Tabela de logs */}
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ticket</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Fila</TableCell>
                <TableCell>Motivo</TableCell>
                <TableCell>Obs</TableCell>
                <TableCell>Pausado por</TableCell>
                <TableCell>Pausado em</TableCell>
                <TableCell>Retorno previsto</TableCell>
                <TableCell>Retomado em</TableCell>
                <TableCell>Duração</TableCell>
                <TableCell>Retomada</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="textSecondary">
                      Nenhuma pausa no período/filtros selecionados
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && logs.map((l) => {
                const reasonName = l.pauseReasonSnapshotName || l.pauseReason?.name || "—";
                const color = l.pauseReason?.color || "#FF9800";
                return (
                  <TableRow key={l.id}>
                    <TableCell>{l.ticketId}</TableCell>
                    <TableCell>{l.ticket?.contact?.name || "—"}</TableCell>
                    <TableCell>{l.ticket?.queue?.name || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={reasonName}
                        className={classes.reasonChip}
                        style={{ backgroundColor: color }}
                      />
                    </TableCell>
                    <TableCell style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {l.pauseNote || "—"}
                    </TableCell>
                    <TableCell>{l.pausedByUser?.name || "—"}</TableCell>
                    <TableCell>{formatDateTime(l.createdAt)}</TableCell>
                    <TableCell>{formatDateTime(l.pauseUntil)}</TableCell>
                    <TableCell>{l.resumedAt ? formatDateTime(l.resumedAt) : "—"}</TableCell>
                    <TableCell>
                      {l.durationSeconds != null
                        ? formatDuration(l.durationSeconds)
                        : <Chip size="small" label="Em pausa" />}
                    </TableCell>
                    <TableCell>{l.resumeType || "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>

        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
          />
        </Box>
      </Paper>
    </MainContainer>
  );
};

export default PauseReport;
